import { useTranslation } from 'react-i18next';
import { Cookie, Info, Settings, BarChart3, Shield, CheckCircle2 } from 'lucide-react';
import { Link } from 'react-router-dom';

interface CookieItem {
  name: string;
  type: string;
  duration: string;
  purpose: string;
}

export function CookiesPage() {
  const { t } = useTranslation();

  const cookieTypes = [
    {
      id: 'essential',
      icon: Shield,
      color: 'from-green-500 to-emerald-500',
      required: true,
    },
    {
      id: 'functional',
      icon: Settings,
      color: 'from-blue-500 to-cyan-500',
      required: false,
    },
    {
      id: 'analytics',
      icon: BarChart3,
      color: 'from-purple-500 to-violet-500',
      required: false,
    },
    {
      id: 'marketing',
      icon: Cookie,
      color: 'from-orange-500 to-amber-500',
      required: false,
    },
  ];

  const cookieList = t('cookiesPage.list.cookies', { returnObjects: true }) as CookieItem[];

  return (
    <div className="min-h-screen">
      {/* Hero */}
      <section className="section-lg relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-accent/5 via-transparent to-transparent" />

        <div className="container relative">
          <div className="max-w-3xl mx-auto text-center">
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-accent/10 border border-accent/20 text-accent text-sm mb-6">
              <Cookie size={16} />
              <span>{t('cookiesPage.lastUpdated')}: 28 December 2025</span>
            </div>

            <h1 className="heading-xl mb-6">
              {t('cookiesPage.title')}{' '}
              <span className="text-accent">{t('cookiesPage.titleHighlight')}</span>
            </h1>
            <p className="text-lg text-text-secondary">
              {t('cookiesPage.intro')}
            </p>
          </div>
        </div>
      </section>

      {/* What are Cookies */}
      <section className="section bg-bg-surface">
        <div className="container">
          <div className="max-w-3xl mx-auto">
            <div className="card p-8 border-accent/20 bg-gradient-to-br from-accent/5 to-transparent">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 rounded-xl bg-accent/10 flex items-center justify-center flex-shrink-0">
                  <Info className="text-accent" size={24} />
                </div>
                <div>
                  <h2 className="text-xl font-semibold mb-3">{t('cookiesPage.whatIs.title')}</h2>
                  <p className="text-text-secondary leading-relaxed">
                    {t('cookiesPage.whatIs.content')}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Cookie Types */}
      <section className="section">
        <div className="container">
          <div className="max-w-4xl mx-auto">
            <h2 className="heading-lg mb-8 text-center">{t('cookiesPage.types.title')}</h2>

            <div className="grid md:grid-cols-2 gap-6">
              {cookieTypes.map((cookie) => (
                <div key={cookie.id} className="card p-6 hover:border-accent/30 transition-colors">
                  <div className="flex items-start gap-4">
                    <div className={`w-12 h-12 rounded-xl bg-gradient-to-br ${cookie.color} flex items-center justify-center flex-shrink-0`}>
                      <cookie.icon className="text-white" size={24} />
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-2">
                        <h3 className="font-semibold">
                          {t(`cookiesPage.types.${cookie.id}.title`)}
                        </h3>
                        {cookie.required && (
                          <span className="text-xs px-2 py-0.5 rounded-full bg-green-500/10 text-green-400 border border-green-500/30">
                            {t('cookiesPage.types.essential.required')}
                          </span>
                        )}
                      </div>
                      <p className="text-text-secondary text-sm">
                        {t(`cookiesPage.types.${cookie.id}.description`)}
                      </p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Cookie List Table */}
      <section className="section bg-bg-surface">
        <div className="container">
          <div className="max-w-4xl mx-auto">
            <h2 className="heading-lg mb-8 text-center">{t('cookiesPage.list.title')}</h2>

            <div className="card overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-bg-elevated border-b border-border-default">
                    <tr>
                      <th className="text-left p-4 text-sm font-semibold text-text-primary">{t('cookiesPage.list.headers.name')}</th>
                      <th className="text-left p-4 text-sm font-semibold text-text-primary">{t('cookiesPage.list.headers.type')}</th>
                      <th className="text-left p-4 text-sm font-semibold text-text-primary">{t('cookiesPage.list.headers.duration')}</th>
                      <th className="text-left p-4 text-sm font-semibold text-text-primary">{t('cookiesPage.list.headers.purpose')}</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-border-default">
                    {cookieList.map((cookie, index) => (
                      <tr key={index} className="hover:bg-bg-elevated/50 transition-colors">
                        <td className="p-4">
                          <code className="text-sm text-accent bg-accent/10 px-2 py-1 rounded">{cookie.name}</code>
                        </td>
                        <td className="p-4 text-sm text-text-secondary">{cookie.type}</td>
                        <td className="p-4 text-sm text-text-secondary">{cookie.duration}</td>
                        <td className="p-4 text-sm text-text-secondary">{cookie.purpose}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* How to Manage */}
      <section className="section">
        <div className="container">
          <div className="max-w-3xl mx-auto">
            <h2 className="heading-lg mb-8 text-center">{t('cookiesPage.management.title')}</h2>

            <div className="space-y-4">
              <div className="card p-6">
                <div className="flex items-start gap-4">
                  <CheckCircle2 className="text-green-500 flex-shrink-0 mt-1" size={20} />
                  <div>
                    <h3 className="font-semibold mb-2">{t('cookiesPage.management.browser.title')}</h3>
                    <p className="text-text-secondary text-sm">
                      {t('cookiesPage.management.browser.description')}
                    </p>
                  </div>
                </div>
              </div>

              <div className="card p-6">
                <div className="flex items-start gap-4">
                  <CheckCircle2 className="text-green-500 flex-shrink-0 mt-1" size={20} />
                  <div>
                    <h3 className="font-semibold mb-2">{t('cookiesPage.management.banner.title')}</h3>
                    <p className="text-text-secondary text-sm">
                      {t('cookiesPage.management.banner.description')}
                    </p>
                  </div>
                </div>
              </div>

              <div className="card p-6">
                <div className="flex items-start gap-4">
                  <CheckCircle2 className="text-green-500 flex-shrink-0 mt-1" size={20} />
                  <div>
                    <h3 className="font-semibold mb-2">{t('cookiesPage.management.optout.title')}</h3>
                    <p className="text-text-secondary text-sm">
                      {t('cookiesPage.management.optout.description')}{' '}
                      <a
                        href="https://tools.google.com/dlpage/gaoptout"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-accent hover:underline"
                      >
                        {t('cookiesPage.management.optout.link')}
                      </a>
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Related Links */}
      <section className="section bg-bg-surface">
        <div className="container">
          <div className="max-w-2xl mx-auto text-center">
            <h2 className="heading-md mb-6">{t('cookiesPage.relatedPolicies.title')}</h2>
            <div className="flex flex-wrap justify-center gap-4">
              <Link to="/privacy" className="btn btn-secondary">
                {t('cookiesPage.relatedPolicies.privacy')}
              </Link>
              <Link to="/privacy#kvkk" className="btn btn-secondary">
                {t('cookiesPage.relatedPolicies.kvkk')}
              </Link>
              <Link to="/terms" className="btn btn-secondary">
                {t('cookiesPage.relatedPolicies.terms')}
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
